import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { ArrowLeft, Camera, MapPin, Briefcase, GraduationCap, Save, Users, Eye, EyeOff, Check, X, GripVertical, Loader2, Library, Play } from 'lucide-react';
import { motion, Reorder, useDragControls } from 'motion/react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { IdentityExpansionModal } from './IdentityExpansionModal';
import { identityData, categoryStyles, getCategoryByTag } from '../constants/identities';
import { ImageCropperModal } from './ImageCropperModal';
import { MediaTrimmerModal } from './MediaTrimmerModal';


import { useUserData } from './UserDataContext';
import { useSettings } from './SettingsContext';
import { useTheme } from './ThemeProvider';
import { calculateAge } from './utils/timeFormat';
import { toast } from 'sonner';
import { AuthService, uploadProfilePhoto } from '../services/api';
import { UsernameService } from '../services/usernameService';
import { FlagImage, allCountries } from './utils/countryFlags';
import { ParallaxBackground } from './utils/parallax';
import { DEFAULT_BANNER_URL } from '../constants/assets';
import { uploadProfileBanner } from '../services/api';
import { IdentityTag } from './IdentityTag';
import { SCREENS } from '../constants/navigation';
import { AllFlagsScreenWrapper } from './AllFlagsScreenWrapper';

interface EditProfileScreenProps {
  onBack: () => void;
  onAllFlags: () => void;
}

const IdentityReorderList = memo(({ identities, onReorder }: { identities: string[], onReorder: (newOrder: string[]) => void }) => {
  return (
    <Reorder.Group
      axis="y"
      values={identities}
      onReorder={onReorder}
      className="space-y-3"
      layoutScroll
    >
      {identities.map((identity) => (
        <IdentityItem key={identity} identity={identity} />
      ))}
    </Reorder.Group>
  );
});

const IdentityItem = ({ identity }: { identity: string }) => {
  const controls = useDragControls();
  
  // Normalize variations to the joint canonical name
  const normalizedTag = (identity === 'Mandinka' || identity === 'Malinke' || identity === 'Mandinka/Malinke') 
    ? 'Malinke/Mandinka' 
    : (identity === 'Soninke' || identity === 'Sarahule' || identity === 'Soninke/Sarahule')
    ? 'Soninke/Sarahule'
    : (identity === 'Diola' || identity === 'Jola' || identity === 'Diola/Jola')
    ? 'Diola/Jola'
    : (identity === 'Dyula' || identity === 'Dioula' || identity === 'Dyula/Dioula')
    ? 'Dyula/Dioula'
    : (identity === 'Vodun' || identity === 'Voodoo' || identity === 'Voodoo/Vodun')
    ? 'Voodoo/Vodun'
    : identity;

  const category = getCategoryByTag(normalizedTag);
  const theme = categoryStyles[category] || categoryStyles['Interests'];

  return (
    <Reorder.Item
      value={identity}
      whileDrag={{
        scale: 1.02,
        boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.4)",
        zIndex: 50,
      }}
      transition={{ type: "spring", stiffness: 500, damping: 30, mass: 1 }}
      className="flex items-center gap-4 px-5 py-3 rounded-2xl text-white cursor-grab active:cursor-grabbing touch-none select-none group border border-white/20 shadow-sm"
      style={{ 
        backgroundImage: theme.gradient,
        textShadow: '0 1px 2px rgba(0,0,0,0.5)'
      }}
    >
      <GripVertical className="w-5 h-5 text-white/50 group-hover:text-white transition-colors shrink-0" />
      <span className="flex-1 text-[15px] font-bold tracking-tight">{identity}</span>
    </Reorder.Item>
  );
};

export function EditProfileScreen({ onBack, onAllFlags }: EditProfileScreenProps) {
  const { theme } = useTheme();
  const identityGradient = 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)';
  const { userProfile, updateProfile, canChangeUsername } = useUserData();
  const {
    showFriendList,
    setShowFriendList,
    friendListVisibility,
    setFriendListVisibility,
    showTribesList,
    setShowTribesList,
    tribeListVisibility,
    setTribeListVisibility,
    showYaps,
    setShowYaps,
    showInDiscover,
    setShowInDiscover,
    shareLocation,
    setShareLocation
  } = useSettings();

  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [showAllFlagsModal, setShowAllFlagsModal] = useState(false);
  const [selectedIdentityCategory, setSelectedIdentityCategory] = useState('');
  const [showTribes, setShowTribes] = useState(userProfile.showTribes ?? true);

  const [pendingCountryChange, setPendingCountryChange] = useState<{ name: string; code: string; flag: string } | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');

  const [profileData, setProfileData] = useState({
    name: userProfile.name,
    username: userProfile.username.replace('@', ''),
    bio: userProfile.bio,
    socials: {
      instagram: (userProfile as any).socials?.instagram || '',
      snapchat:  (userProfile as any).socials?.snapchat  || '',
      tiktok:    (userProfile as any).socials?.tiktok    || '',
      x:         (userProfile as any).socials?.x         || '',
    },
    location: userProfile.location,
    home_residence: (userProfile as any).home_residence || userProfile.location || userProfile.country || '', // Autofill from legacy location or origin country
    education: userProfile.education,
    occupation: userProfile.occupation,
    country: userProfile.country,
    countryFlag: userProfile.countryFlag || '', // Heritage/origin flag - NEVER overwritten by residence
    hideLocation: ((userProfile as any).showLocationSetAt != null && new Date((userProfile as any).showLocationSetAt) > new Date('2026-03-30T00:00:00Z'))
      ? (userProfile.showLocation === false)    // user recently explicitly set it to false -> means they HIDE it
      : false,                                  // default to not hiding
    // Adults only (see the toggle). Stored inverted as showAge, matching
    // showLocation, so an absent field reads as "shown".
    hideAge: (userProfile as any).showAge === false,
    identities: userProfile.identities,
    portfolio: userProfile.portfolio || [],
    portfolioPreviews: userProfile.portfolioPreviews || [],
    profilePictureIndex: userProfile.profilePictureIndex || 0,
    portfolioVideos: userProfile.portfolioVideos || [],
    photoMetadata: userProfile.photoMetadata || [],
    bannerImage: userProfile.bannerImage || '',
    coverImage: userProfile.coverImage || '',
    dateOfBirth: userProfile.dateOfBirth || '',
  });

  // Sync showLocation from userProfile whenever it changes (e.g. after healing async-writes true)
  useEffect(() => {
    setProfileData(prev => ({
      ...prev,
      hideLocation: ((userProfile as any).showLocationSetAt != null && new Date((userProfile as any).showLocationSetAt) > new Date('2026-03-30T00:00:00Z'))
        ? (userProfile.showLocation === false)  // user explicitly set it recently
        : false,                                 // default to DO NOT hide
      hideAge: (userProfile as any).showAge === false,
    }));
  }, [userProfile.showLocation]);

  // Media State
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [mediaStep, setMediaStep] = useState<'match' | 'square'>('match');
  const [tempMatchPhoto, setTempMatchPhoto] = useState<string | null>(null);
  const [tempVideoBlob, setTempVideoBlob] = useState<Blob | null>(null);
  const [tempTrimStart, setTempTrimStart] = useState<number | undefined>(undefined);
  const [tempTrimEnd, setTempTrimEnd] = useState<number | undefined>(undefined);
  const [originalMediaBlob, setOriginalMediaBlob] = useState<Blob | null>(null);

  const [uploading, setUploading] = useState<boolean[]>(Array(4).fill(false));
  // Tracks any avatar-media add/remove in this editing session. Uploads persist
  // immediately (so isProfileDirty goes false), which would otherwise disable the
  // Save button right after the user changes a photo — this keeps it responsive.
  const [mediaChangedThisSession, setMediaChangedThisSession] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [cropType, setCropType] = useState<'portfolio' | 'banner'>('portfolio');
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [tempFileSignature, setTempFileSignature] = useState<string | null>(null);

  const dataURLToBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Debounce username check
  useEffect(() => {
    const checkUsername = async () => {
      const currentUsername = profileData.username;

      // If username hasn't changed from current profile, it's valid (it's their own)
      if (`@${currentUsername}` === userProfile.username) {
        setUsernameStatus('idle');
        return;
      }

      if (currentUsername.length < 4) {
        setUsernameStatus('invalid');
        return;
      }

      setUsernameStatus('checking');
      try {
        // 🔒 PATCH 6A: Use UsernameService for real-time availability check
        const isAvailable = await UsernameService.checkUsernameAvailability(currentUsername);
        setUsernameStatus(isAvailable ? 'available' : 'taken');
      } catch (error) {
        console.error('Error checking username:', error);
        setUsernameStatus('idle'); // Reset on error
      }
    };

    const timeoutId = setTimeout(() => {
      if (profileData.username) {
        checkUsername();
      } else {
        setUsernameStatus('idle');
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [profileData.username, userProfile.username]);

  // Listen for flag selection from AllFlagsScreen
  useEffect(() => {
    const handleFlagSelected = (e: any) => {
      const { name, code } = e.detail;
      setProfileData(prev => ({
        ...prev,
        country: name,
        countryFlag: code
      }));
    };

    window.addEventListener('flagSelected', handleFlagSelected);
    return () => window.removeEventListener('flagSelected', handleFlagSelected);
  }, []);


  // Ensure the currently selected country is always visible as one of the 3 featured flags
  const defaultCountryOptions = [
    { name: 'Senegal', code: 'sn' },
    { name: 'Uganda', code: 'ug' },
    { name: 'Egypt', code: 'eg' },
  ];

  const countryOptions = [...defaultCountryOptions];
  if (profileData.country && !countryOptions.some(c => c.name === profileData.country)) {
    countryOptions[2] = { name: profileData.country, code: profileData.countryFlag || '' };
  }

  const handleInputChange = (field: keyof typeof profileData, value: string | boolean | string[]) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  // Only adults may hide their age. Computed from the stored date of birth
  // rather than any editable form value, since DOB is locked after account
  // creation and a form field could otherwise be used to unlock the toggle.
  // Fails closed: an unreadable or missing DOB is treated as a minor.
  const canHideAge = (calculateAge(userProfile.dateOfBirth) ?? 0) >= 18;

  const handleCountryChange = (country: { name: string; code: string }) => {
    // 🏳️ ORIGIN FLAG RULE: The country flag picker ALWAYS sets origin/heritage.
    // It updates both `country` (origin name) and `countryFlag` (origin flag code).
    // This is COMPLETELY separate from `home_residence` (where they live today).
    setProfileData(prev => ({ 
        ...prev, 
        country: country.name,      // Your origin country name
        countryFlag: country.code   // Your origin flag code
    }));
    setPendingCountryChange(null);
  };

  const handleIdentitiesReorder = useCallback((newOrder: string[]) => {
    handleInputChange('identities', newOrder);
  }, []);

  const handleIdentitiesSave = async (identities: string[]) => {
    setProfileData((prev) => ({ ...prev, identities }));
    // 🔒 PATCH: Sync identity order immediately to backend to ensure consistency
    try {
      await updateProfile({ identities });
      toast.success('Identity order updated');
    } catch (error) {
      console.error('Failed to sync identity order:', error);
      toast.error('Failed to save identity order');
    }
  };

  const handleSlotClick = (index: number) => {
    setActiveSlot(index);
    fileInputRef.current?.click();
  };

  // Remove a single avatar-media item. The arrays are slot-based (fixed length 4),
  // so we splice out the chosen index and pad back to 4 — this naturally reflows
  // the remaining cards to the front. Persisted only when the user hits Save.
  const handleRemoveMedia = (index: number) => {
    setProfileData(prev => {
      const compact = (arr: any[]) => {
        const a = (arr || []).slice(0, 4);
        a.splice(index, 1);
        while (a.length < 4) a.push('');
        return a;
      };
      const newPortfolio = compact(prev.portfolio);
      const newPreviews = compact(prev.portfolioPreviews || []);
      const newVideos = compact(prev.portfolioVideos || []);
      // Drop the removed slot's metadata and shift higher slots down by one.
      const newMeta = (prev.photoMetadata || [])
        .filter((m: any) => Number(m.slot) !== index)
        .map((m: any) => (Number(m.slot) > index ? { ...m, slot: Number(m.slot) - 1 } : m));
      // Keep the avatar pointer valid after the reflow.
      let newIndex = prev.profilePictureIndex;
      if (index === newIndex) newIndex = 0;
      else if (index < newIndex) newIndex = newIndex - 1;
      if (!newPortfolio[newIndex]) {
        const firstPopulated = newPortfolio.findIndex((p: any) => p);
        newIndex = firstPopulated >= 0 ? firstPopulated : 0;
      }
      return {
        ...prev,
        portfolio: newPortfolio,
        portfolioPreviews: newPreviews,
        portfolioVideos: newVideos,
        photoMetadata: newMeta,
        profilePictureIndex: newIndex,
      };
    });
    setMediaChangedThisSession(true);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || activeSlot === null || !userProfile.id) return;

    // Validate file type and size (max 10MB for video support)
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Please upload an image or video file');
      return;
    }
    const maxSize = file.type.startsWith('video/') ? 15 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File size should be less than ${file.type.startsWith('video/') ? '15MB' : '10MB'}`);
      return;
    }

    // Guard against concurrent uploads
    if (uploading[activeSlot]) return;

    // Create a signature to track the file uniquely (slot + name + size)
    const signature = `${activeSlot}-${file.name}-${file.size}`;
    setTempFileSignature(signature);
    setMediaType(file.type.startsWith('video/') ? 'video' : 'image');

    // Read file for MediaTrimmerModal - Using URL.createObjectURL for better video performance
    const objectUrl = URL.createObjectURL(file);
    setOriginalMediaBlob(file);
    setMediaUrl(objectUrl);
    setMediaStep('match'); // Start with Match Pane crop
    setMediaModalOpen(true);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBannerSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userProfile.id) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Banner size should be less than 5MB');
      return;
    }

    setCropType('banner');
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result as string);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);

    if (bannerInputRef.current) bannerInputRef.current.value = '';
  };

  const handleMediaSave = async (data: { photoUrl: string; videoBlob?: Blob; pickedStillUrl?: string; trimStart?: number; trimEnd?: number }) => {
    const { photoUrl, videoBlob, pickedStillUrl, trimStart, trimEnd } = data;
    if (!userProfile.id || activeSlot === null) {
      console.log('[EditProfile] handleMediaSave aborted: missing id or slot', { mediaStep, activeSlot });
      return;
    }

    const slot = activeSlot; // Local constant for type safety
    if (mediaStep === 'match') {
      // Save the match pane crop and proceed to square crop
      console.log('[EditProfile] Match pane crop captured. Moving to square crop...');
      setTempMatchPhoto(photoUrl);
      if (videoBlob) setTempVideoBlob(videoBlob);
      if (trimStart !== undefined) setTempTrimStart(trimStart);
      if (trimEnd !== undefined) setTempTrimEnd(trimEnd);
      
      // If it's a video, for step 2 (Square) we MUST use the still frame they just picked
      if (pickedStillUrl) {
        console.log('[EditProfile] Using picked still frame for square crop');
        setMediaUrl(pickedStillUrl);
        setMediaType('image');
      } else {
        console.log('[EditProfile] No pickedStillUrl, using photoUrl for square crop');
        setMediaUrl(photoUrl);
        setMediaType('image'); // Force image for square step
      }

      setMediaStep('square');
      toast.info('Full card framed! Now crop for your profile preview (Square)');
      return; 
    } else {
      // Final step: Upload both images
      console.log('[EditProfile] Square crop captured. Starting upload stack...');
      setMediaModalOpen(false);

      if (!tempMatchPhoto) {
        toast.error('Match crop is missing. Please try again.');
        return;
      }

      setUploading(prev => {
        const newUploading = [...prev];
        newUploading[slot] = true;
        return newUploading;
      });

      try {
        // 1. Upload Match Pane version (Vertical)
        const matchBlob = dataURLToBlob(tempMatchPhoto);
        const matchFile = new File([matchBlob], `photo-${slot}-match.jpg`, { type: 'image/jpeg' });
        const matchURL = await uploadProfilePhoto(String(userProfile.id), matchFile, slot);

        // 2. Upload Profile Preview version (Square)
        const squareBlob = dataURLToBlob(photoUrl);
        const squareFile = new File([squareBlob], `photo-${slot}-square.jpg`, { type: 'image/jpeg' });
        const previewURL = await uploadProfilePhoto(String(userProfile.id), squareFile, slot, 'preview');

        // 3. Handle Video Upload if present
        let videoURL = profileData.portfolioVideos?.[slot] || '';
        if (tempVideoBlob) {
           // Preserve the source's real content-type/extension so Firebase Storage
           // serves it correctly. Force-labelling everything video/mp4 made iPhone
           // .mov / .webm uploads unplayable (wrong Content-Type → decode fails).
           const vType = tempVideoBlob.type?.startsWith('video/') ? tempVideoBlob.type : 'video/mp4';
           const vExt = vType.includes('webm') ? 'webm' : vType.includes('quicktime') ? 'mov' : vType.includes('ogg') ? 'ogv' : 'mp4';
           const videoFile = new File([tempVideoBlob], `video-${slot}.${vExt}`, { type: vType });
           videoURL = await uploadProfilePhoto(String(userProfile.id), videoFile, slot, 'video');
        }

        // Update photo metadata
        const updatedMeta = (profileData.photoMetadata || []).filter(m => Number(m.slot) !== Number(slot));
        const metaEntry: any = { slot, timestamp: Date.now() };
        if (tempFileSignature) metaEntry.signature = tempFileSignature;
        if (tempTrimStart !== undefined) metaEntry.trimStart = tempTrimStart;
        if (tempTrimEnd !== undefined) metaEntry.trimEnd = tempTrimEnd;
        updatedMeta.push(metaEntry);

        // Ensure arrays are exactly 4 elements and contain no undefined values
        const sanitizeArray = (arr: any[]) => {
          const newArr = Array(4).fill('');
          for (let i = 0; i < 4; i++) {
            if (arr[i]) newArr[i] = arr[i];
          }
          return newArr;
        };

        const newPortfolio = sanitizeArray([...profileData.portfolio]);
        newPortfolio[slot] = matchURL;

        const newPreviews = sanitizeArray(profileData.portfolioPreviews || []);
        newPreviews[slot] = previewURL;

        const newVideos = sanitizeArray(profileData.portfolioVideos || []);
        newVideos[slot] = videoURL;

        // Smart auto-set profile picture
        let newIndex = profileData.profilePictureIndex;
        if (!newPortfolio[newIndex]) newIndex = slot;

        console.log('[MediaUpload] Finalizing profile update in Firestore...');
        await updateProfile({
          portfolio: newPortfolio,
          portfolioPreviews: newPreviews,
          portfolioVideos: newVideos,
          photoMetadata: updatedMeta,
          profilePictureIndex: newIndex
        });

        // 🟢 Update local state only AFTER successful persistence
        setProfileData(prev => ({
          ...prev,
          portfolio: newPortfolio,
          portfolioPreviews: newPreviews,
          portfolioVideos: newVideos,
          photoMetadata: updatedMeta,
          profilePictureIndex: newIndex
        }));
        setMediaChangedThisSession(true);

        setUploading(prev => {
          const newUploading = [...prev];
          newUploading[slot] = false;
          return newUploading;
        });

        toast.success('Media updated and saved!');
      } catch (error: any) {
        console.error('Error uploading media stack:', error);
        // Specialized error messaging
        if (error?.message?.includes('storage/unauthorized')) {
          toast.error('Permission denied. Please try logging in again.');
        } else {
          toast.error(`Failed to upload media: ${error.message || 'Unknown error'}`);
        }
      } finally {
        setUploading(prev => {
          const newUploading = [...prev];
          newUploading[slot] = false;
          return newUploading;
        });
        setMediaUrl(prev => {
          if (prev && prev.startsWith('blob:')) {
            URL.revokeObjectURL(prev);
          }
          return null;
        });
        setMediaStep('match');
        setTempFileSignature(null);
        setTempTrimStart(undefined);
        setTempTrimEnd(undefined);
        setOriginalMediaBlob(null);
      }
    }
  };

  const handleCropSave = async (croppedImageUrl: string) => {
    if (!userProfile.id) return;

    if (cropType === 'banner') {
      setCropModalOpen(false);
      setIsBannerUploading(true);
      try {
        const blob = dataURLToBlob(croppedImageUrl);
        const file = new File([blob], "banner.jpg", { type: "image/jpeg" });
        const bannerUrl = await uploadProfileBanner(String(userProfile.id), file);
        
        setProfileData(prev => ({ ...prev, bannerImage: bannerUrl, coverImage: bannerUrl }));
        await updateProfile({ bannerImage: bannerUrl, coverImage: bannerUrl });
        toast.success('Banner updated successfully!');
      } catch (error) {
        console.error('Error uploading banner:', error);
        toast.error('Failed to upload banner');
      } finally {
        setIsBannerUploading(false);
        setCropImage(null);
      }
      return;
    }
  };

  const handleSave = async () => {
    // 🔒 PATCH 7C: Validate portfolio and profilePictureIndex before saving
    const validPortfolio = profileData.portfolio.filter(p => p); // Remove null/undefined
    const safeProfilePictureIndex = Math.max(0, Math.min(
      profileData.profilePictureIndex,
      validPortfolio.length - 1
    ));

    // Strip @-prefix and trim each social handle. Empty strings are kept (Firestore
     // will simply store empty values; render code filters them out).
    const cleanedSocials = {
      instagram: (profileData.socials.instagram || '').trim().replace(/^@+/, ''),
      snapchat:  (profileData.socials.snapchat  || '').trim().replace(/^@+/, ''),
      tiktok:    (profileData.socials.tiktok    || '').trim().replace(/^@+/, ''),
      x:         (profileData.socials.x         || '').trim().replace(/^@+/, ''),
    };

    const updatedData: any = {
      name: profileData.name?.trim().replace(/\s+/g, ' '),
      bio: profileData.bio,
      socials: cleanedSocials,
      location: profileData.location,
      home_residence: profileData.home_residence, // Where they live (for location text)
      education: profileData.education,
      occupation: profileData.occupation,
      country: profileData.country,           // Origin country name
      countryFlag: profileData.countryFlag,   // Origin flag code — NEVER overwritten by residence
      identities: profileData.identities,
      showInDiscover: showInDiscover,
      showTribes: showTribes,
      showLocation: !profileData.hideLocation, // Whether to show location on posts (inverted from UI state)
      showLocationSetAt: new Date().toISOString(),    // Marks this as an intentional user choice
      // Minors are never allowed to hide their age, so force it shown for them
      // regardless of any value the form is carrying.
      showAge: canHideAge ? !profileData.hideAge : true,
      // 🔒 SOCIAL PRIVACY: Persistence for friend and tribe list visibility
      showFriendList: showFriendList,
      showFriendListSetAt: new Date().toISOString(),
      friendListVisibility: friendListVisibility,
      showTribesList: showTribesList,
      showTribesListSetAt: new Date().toISOString(),
      tribeListVisibility: tribeListVisibility,
      showYaps: showYaps,
      showYapsSetAt: new Date().toISOString(),
      portfolio: profileData.portfolio,
      // Keep `avatar` in step with the chosen photo.
      //
      // It was written ONCE, by ProfileSetupScreen at signup, and never again —
      // this screen updated portfolio/portfolioPreviews and left `avatar`
      // pointing at whatever the signup photo was. Replace that photo and the
      // old Storage URL 404s, so every surface that reads `avatar` (yaps and
      // comments resolve it as croppedAvatar || avatar, with no portfolio
      // fallback) fell through to the initial-letter placeholder. That is the
      // blank-avatar-with-a-letter case.
      //
      // Recomputed from the same source ProfileSetup uses, but honouring
      // profilePictureIndex rather than assuming slot 0. Only written when it
      // actually resolves to something: an empty value here would wipe a working
      // avatar rather than repair a broken one.
      ...(() => {
        const idx = profileData.profilePictureIndex ?? 0;
        const next =
          profileData.portfolioPreviews?.[idx] ||
          profileData.portfolio?.[idx] ||
          profileData.portfolioPreviews?.find((p: string) => p && p.trim()) ||
          profileData.portfolio?.find((p: string) => p && p.trim()) ||
          '';
        return next ? { avatar: next } : {};
      })(),
      portfolioPreviews: profileData.portfolioPreviews,
      portfolioVideos: profileData.portfolioVideos,
      photoMetadata: profileData.photoMetadata,
      profilePictureIndex: validPortfolio.length > 0 ? safeProfilePictureIndex : 0,
      bannerImage: profileData.bannerImage,
      coverImage: profileData.coverImage || profileData.bannerImage,
      dateOfBirth: profileData.dateOfBirth,
    };

    // Handle username change with restriction
    const robustUsername = `@${profileData.username?.trim().replace(/^@+/, '')}`;
    const usernameChanged = robustUsername !== userProfile.username;
    if (usernameChanged) {
      if (canChangeUsername()) {
        if (usernameStatus === 'taken') {
          toast.error('Username is already taken');
          return;
        }
        if (usernameStatus === 'invalid') {
          toast.error('Username must be at least 4 characters');
          return;
        }
        if (usernameStatus === 'checking') {
          toast.error('Please wait for username check to complete');
          return;
        }
        if (usernameStatus === 'idle' && profileData.username !== userProfile.username.replace('@', '')) {
          toast.error('Please enter a valid username or wait for validation.');
          return;
        }

        updatedData.username = robustUsername;
        // 🔒 PATCH 2A: Removed client-side timestamp - UserDataContext handles it
        toast.success('Profile updated! You can change your username again in 30 days.');
      } else {
        toast.error('You can only change your username once every 30 days.');
        return;
      }
    } else {
      toast.success('Profile updated successfully!');
    }

    // 🔒 PATCH 2B: Wrap in try-catch to handle server-side validation errors
    try {
      await updateProfile(updatedData);
      onBack();
    } catch (error: any) {
      // Display specific error messages from updateProfile
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    }
  };

  const handleBannerUpdate = (bannerUrl: string) => {
    setProfileData(prev => ({ ...prev, bannerImage: bannerUrl, coverImage: bannerUrl }));
    // 🔒 PATCH: Persist immediately for responsiveness
    updateProfile({ bannerImage: bannerUrl, coverImage: bannerUrl });
  };

  // Single source of truth for "are there unsaved changes?" — used by both the
  // back-discard prompt and the Save button's enabled state. Includes the avatar
  // media arrays so adding/removing media (re)enables Save.
  const isProfileDirty = () => {
    const norm = (arr: any[]) => {
      const a = Array(4).fill('');
      for (let i = 0; i < 4; i++) if ((arr || [])[i]) a[i] = (arr || [])[i];
      return JSON.stringify(a);
    };
    const mediaChanged =
      norm(profileData.portfolio) !== norm(userProfile.portfolio || []) ||
      norm(profileData.portfolioPreviews) !== norm(userProfile.portfolioPreviews || []) ||
      norm(profileData.portfolioVideos) !== norm((userProfile as any).portfolioVideos || []);

    // Socials are compared @-stripped and trimmed on both sides, matching what
    // handleSave actually writes — otherwise a stored "@handle" would read as a
    // change forever and leave Save permanently enabled.
    const cleanHandle = (v: any) => String(v || '').trim().replace(/^@+/, '');
    const socialsChanged = (['instagram', 'snapchat', 'tiktok', 'x'] as const).some(
      k => cleanHandle((profileData.socials as any)[k]) !== cleanHandle(((userProfile as any).socials || {})[k]),
    );

    return (
      profileData.name !== userProfile.name ||
      profileData.username !== userProfile.username.replace('@', '') ||
      profileData.bio !== userProfile.bio ||
      // Each of these four is written by handleSave but was absent here, so
      // editing one on its own left the Save button disabled — the change was
      // unsaveable rather than merely unsaved.
      socialsChanged ||
      profileData.home_residence !== ((userProfile as any).home_residence || userProfile.location || userProfile.country || '') ||
      profileData.hideAge !== ((userProfile as any).showAge === false) ||
      showYaps !== ((userProfile as any).showYaps ?? true) ||
      profileData.location !== userProfile.location ||
      profileData.education !== userProfile.education ||
      profileData.occupation !== userProfile.occupation ||
      profileData.country !== userProfile.country ||
      profileData.countryFlag !== (userProfile.countryFlag || '') ||
      !profileData.hideLocation !== (userProfile.showLocation ?? true) ||
      JSON.stringify(profileData.identities) !== JSON.stringify(userProfile.identities) ||
      profileData.profilePictureIndex !== (userProfile.profilePictureIndex || 0) ||
      showTribes !== (userProfile.showTribes ?? true) ||
      showInDiscover !== (userProfile.showInDiscover ?? true) ||
      showFriendList !== ((userProfile as any).showFriendList ?? true) ||
      friendListVisibility !== ((userProfile as any).friendListVisibility || 'everyone') ||
      showTribesList !== ((userProfile as any).showTribesList ?? true) ||
      tribeListVisibility !== ((userProfile as any).tribeListVisibility || 'everyone') ||
      profileData.bannerImage !== (userProfile.bannerImage || '') ||
      profileData.coverImage !== (userProfile.coverImage || '') ||
      profileData.dateOfBirth !== (userProfile.dateOfBirth || '') ||
      mediaChanged
    );
  };

  // 🔒 PATCH: Refined handleBack with deep dirty check
  const handleBack = () => {
    const isDirty = isProfileDirty();

    if (isDirty) {
      if (window.confirm("You have unsaved changes. Are you sure you want to discard them?")) {
        onBack();
      }
    } else {
      onBack();
    }
  };

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      {showAllFlagsModal && (
        <div className="absolute inset-0 z-50 bg-white dark:bg-[#0D0D0D]">
          <AllFlagsScreenWrapper onBack={() => setShowAllFlagsModal(false)} />
        </div>
      )}
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      <ScrollArea className="h-full">
        <div className="w-full max-w-2xl mx-auto relative z-10">
          {/* Header */}
          <div
            className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-[#e7d299]/90 dark:bg-black/30 backdrop-blur-lg"
            style={{ paddingTop: 'max(80px, calc(env(safe-area-inset-top) + 48px))' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  className="text-[#2C3E50] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
                >
                  <ArrowLeft className="w-6 h-6" />
                </motion.button>
                <h1 className="text-2xl text-black dark:text-white">
                  Edit Profile
                </h1>
              </div>
              <Button
                onClick={handleSave}
                disabled={!isProfileDirty() && !mediaChangedThisSession}
                className="bg-linear-to-r from-[#4478ea] to-[#2d1ef1] hover:opacity-90 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>

          <div className="p-6">
              {/* Avatar Media Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-black dark:text-[#E5E5E5] uppercase tracking-tight">Avatar Media</h3>
                  <span className="text-xs text-[#999999] font-medium">
                    {(() => {
                      const count = (profileData.portfolio || []).filter(Boolean).length;
                      if (count >= 4) return 'Maximum reached';
                      if (count === 0) return 'Add up to 4 photos or videos';
                      return `Add up to ${4 - count} more photo${4 - count === 1 ? '' : 's'} or videos`;
                    })()}
                  </span>
                </div>
                <p className="text-sm text-[#666666] dark:text-[#a0a0a0] mb-4">
                  Tap one to set as avatar media.
                </p>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,video/*"
              />

              <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="relative aspect-3/4">
                    {profileData.portfolio[index] ? (
                      <div
                        className={`relative w-full h-full rounded-2xl overflow-hidden group border-2 cursor-pointer transition-all ${profileData.profilePictureIndex === index
                          ? 'border-[#4478ea] ring-2 ring-[#4478ea]/20'
                          : 'border-transparent hover:border-black/10 dark:hover:border-white/10'
                          }`}
                        onClick={() => setProfileData(prev => ({ ...prev, profilePictureIndex: index }))}
                      >
                        <img
                          src={profileData.portfolioPreviews?.[index] || profileData.portfolio[index]}
                          alt={`Media ${index + 1}`}
                          className="w-full h-full object-cover object-center"
                        />

                        {/* Avatar Indicator Badge - Pill style */}
                        {profileData.profilePictureIndex === index && (
                          <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md py-0.5 px-2 rounded-full border border-white/20 shadow-lg flex items-center justify-center">
                            <span className="text-[6px] font-bold text-white tracking-wider uppercase">Avatar</span>
                          </div>
                        )}

                        {/* Actions Overlay - Always Visible */}
                        <div className="absolute top-2 right-2 flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSlotClick(index);
                            }}
                            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 shadow-lg border border-white/20"
                            title="Replace media"
                          >
                            <Camera className="w-4 h-4 text-white" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMedia(index);
                            }}
                            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 shadow-lg border border-white/20"
                            title="Remove media"
                            aria-label="Remove media"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>

                        {/* Selection Hint */}
                        {profileData.profilePictureIndex !== index && (
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                            <span className="text-white text-sm font-medium bg-black/50 px-3 py-1 rounded-full">
                              Set as Avatar Media
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => handleSlotClick(index)}
                        disabled={uploading[index]}
                        className="w-full h-full rounded-2xl border-2 border-dashed border-[#4478ea]/40 flex flex-col items-center justify-center gap-2 hover:bg-[#4478ea]/10 transition-colors relative"
                      >
                        {uploading[index] ? (
                          <div className="w-8 h-8 border-2 border-[#4478ea] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-[#4478ea]" />
                            <span className="text-sm text-[#4478ea]">
                              Media {index + 1}
                            </span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Banner Section */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-black dark:text-white">Profile Banner</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => bannerInputRef.current?.click()}
                  className="text-[#4478ea] font-bold"
                >
                  Edit Banner
                </Button>
              </div>
              <div
                className="relative w-full h-32 rounded-2xl overflow-hidden cursor-pointer group shadow-md"
                onClick={() => {
                  setCropType('banner');
                  bannerInputRef.current?.click();
                }}
              >
                <img
                  src={profileData.bannerImage || profileData.coverImage || DEFAULT_BANNER_URL}
                  alt="Banner"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  {isBannerUploading ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </div>
              </div>
              <input
                type="file"
                ref={bannerInputRef}
                onChange={handleBannerSelect}
                className="hidden"
                accept="image/*"
              />
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-black dark:text-white mb-2">Profile Name</label>
                <Input
                  value={profileData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5]"
                />
              </div>

              {/* Username */}
              <div>
                <label className="block text-black dark:text-white mb-2">Username</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666] dark:text-[#E5E5E5]">@</span>
                  <Input
                    value={profileData.username}
                    onChange={(e) => handleInputChange('username', e.target.value.replace(/[^a-zA-Z0-9_.]/g, '').toLowerCase())}
                    className={`pl-7 bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] ${!canChangeUsername() ? 'opacity-50 cursor-not-allowed' : ''} ${usernameStatus === 'taken' ? 'border-red-500 focus:border-red-500' :
                      usernameStatus === 'available' ? 'border-green-500 focus:border-green-500' : ''
                      }`}
                    disabled={!canChangeUsername()}
                    maxLength={20}
                  />
                  {usernameStatus === 'checking' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-[#4478ea] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  {usernameStatus === 'available' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                  {usernameStatus === 'taken' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                  )}
                </div>
                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${usernameStatus === 'taken' ? 'text-red-500' :
                    usernameStatus === 'available' ? 'text-green-500' :
                      'text-[#666666] dark:text-[#a0a0a0]'
                    }`}>
                    {usernameStatus === 'taken' ? 'Username taken' :
                      usernameStatus === 'available' ? 'Username available' :
                        usernameStatus === 'invalid' ? 'Min 4 characters' :
                          !canChangeUsername() ? 'You can change your username once every 30 days.' : ''}
                  </p>
                  <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">
                    {profileData.username.length}/20
                  </p>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-black dark:text-white mb-2">Bio</label>
                <Textarea
                  value={profileData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={4}
                  maxLength={300}
                  className="bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] resize-none"
                />
                <div className="text-sm text-[#999999] dark:text-[#E5E5E5] mt-1 text-right">
                  {profileData.bio.length}/300
                </div>
              </div>

              {/* Social media handles — empty fields stay hidden on the profile. */}
              <div>
                <label className="block text-black dark:text-white mb-2">Social Media</label>
                <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mb-3">
                  Add only the handles you want shown. Leave blank to hide.
                </p>
                <div className="space-y-3">
                  {([
                    { key: 'instagram', label: 'Instagram', placeholder: 'your_ig_handle' },
                    { key: 'snapchat',  label: 'Snapchat',  placeholder: 'your_snap'      },
                    { key: 'tiktok',    label: 'TikTok',    placeholder: 'your_tiktok'    },
                    { key: 'x',         label: 'X',         placeholder: 'your_handle'    },
                  ] as const).map(({ key, label, placeholder }) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="w-20 text-sm text-[#666666] dark:text-[#E5E5E5]">{label}</span>
                      <span className="text-[#999999]">@</span>
                      <Input
                        type="text"
                        value={(profileData.socials as any)[key]}
                        onChange={(e) =>
                          setProfileData(prev => ({
                            ...prev,
                            socials: { ...prev.socials, [key]: e.target.value.replace(/^@+/, '') },
                          }))
                        }
                        placeholder={placeholder}
                        autoCapitalize="none"
                        autoCorrect="off"
                        className="flex-1 bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5]"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Date of Birth */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-black dark:text-white">Date of Birth</label>
                  {/* Adults only. A minor's age is a safety signal the rest of the
                      app gates on, so it is not theirs to hide; the toggle is not
                      rendered at all rather than shown disabled, which would
                      advertise a control they cannot use. */}
                  {canHideAge && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#666666] dark:text-[#a0a0a0] max-w-[120px] text-right">
                        Hide age
                      </span>
                      <Switch
                        checked={profileData.hideAge}
                        onCheckedChange={(checked: boolean) => handleInputChange('hideAge', checked)}
                        className="data-[state=checked]:bg-[#4478ea]"
                      />
                    </div>
                  )}
                </div>
                <Input
                  type="date"
                  value={profileData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  disabled={!!userProfile.dateOfBirth}
                  className={`bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white ${!!userProfile.dateOfBirth ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <p className="text-xs text-[#999999] dark:text-[#a0a0a0] mt-1">
                  {!!userProfile.dateOfBirth 
                    ? "Date of birth cannot be changed after account creation." 
                    : "Required for age calculation on your profile."}
                </p>
              </div>

              {/* Where You Live (home_residence) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <label className="block text-black dark:text-white">Where You Live</label>
                    <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mt-0.5">This shows as your location on posts. Your flag is always set by your origin below.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#666666] dark:text-[#a0a0a0] max-w-[120px] text-right">
                      Hide from posts
                    </span>
                    <Switch
                      checked={profileData.hideLocation}
                      onCheckedChange={(checked: boolean) => handleInputChange('hideLocation', checked)}
                      className="data-[state=checked]:bg-[#4478ea]"
                    />
                  </div>
                </div>
                <div className="relative">
                  <Select
                    value={profileData.home_residence}
                    onValueChange={(val: string) => handleInputChange('home_residence', val)}
                  >
                    <SelectTrigger className="w-full bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white pl-10">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10">
                         <MapPin className="w-5 h-5 text-[#4478ea]" />
                      </div>
                      <SelectValue placeholder="Select a Country" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 max-h-60">
                      {allCountries.map((country) => (
                        <SelectItem key={country.code} value={country.name} className="text-black dark:text-white cursor-pointer hover:bg-black/5 dark:hover:bg-white/5">
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs mt-1.5">
                  <span className="text-[#666666] dark:text-[#a0a0a0]">Your yaps will show: </span>
                  <span className="font-semibold text-[#4478ea]">
                    {(!profileData.hideLocation)
                      ? (profileData.home_residence?.trim() || userProfile.location?.trim() || userProfile.country?.trim() || 'Earth')
                      : 'Earth'}
                  </span>
                </p>
              </div>

              {/* Education */}
              <div>
                <label className="block text-black dark:text-white mb-2">Education</label>
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4478ea]" />
                  <Input
                    value={profileData.education}
                    onChange={(e) => handleInputChange('education', e.target.value)}
                    className="pl-10 bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5]"
                  />
                </div>
              </div>

              {/* Occupation */}
              <div>
                <label className="block text-black dark:text-white mb-2">Occupation</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4478ea]" />
                  <Input
                    value={profileData.occupation}
                    onChange={(e) => handleInputChange('occupation', e.target.value)}
                    className="pl-10 bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5]"
                  />
                </div>
              </div>

              {/* Country of Origin Flag */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-black dark:text-white font-semibold">Country of Origin (Your Flag 🏳️)</label>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Navigate to All Flags Screen
                      setShowAllFlagsModal(true);
                    }}
                    className="bg-transparent border-2 border-[#4478ea] text-[#4478ea] hover:bg-[#4478ea] hover:text-white text-xs h-8 button-shadow"
                  >
                    All Flags
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {countryOptions.map((country) => (
                    <motion.button
                      key={country.name}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleCountryChange(country)}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 subtle-shadow ${profileData.country === country.name
                        ? 'border-[#4478ea] bg-linear-to-r from-[#4478ea]/20 to-[#2d1ef1]/20'
                        : 'border-black/10 dark:border-white/20 bg-[#F5F5F5] dark:bg-[#1a1a1a] hover:border-[#4478ea]/50'
                        }`}
                    >
                      <div className="w-16 h-12 rounded-lg overflow-hidden border border-black/10 dark:border-white/10">
                        <FlagImage
                          countryName={country.name}
                          size="w80"
                          className={`w-full h-full ${(country.name?.toLowerCase() === 'soulaan' || country.name?.toLowerCase() === 'republic of new afrika' || country.name?.toLowerCase() === 'gullah geechie') ? 'object-contain' : 'object-cover'}`}
                        />
                      </div>
                      <span className="text-xs text-black dark:text-white text-center line-clamp-1">{country.name}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Identity */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-black dark:text-white font-semibold">Your Identity Order</label>
                  <p className="text-[10px] text-[#666666] dark:text-[#a0a0a0] uppercase tracking-widest">Drag to reorder</p>
                </div>
                
                <div className="mb-6 p-4 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-sm">
                  {profileData.identities && profileData.identities.length > 0 ? (
                    <IdentityReorderList 
                      identities={profileData.identities} 
                      onReorder={handleIdentitiesReorder} 
                    />
                  ) : (
                    <p className="text-sm text-[#999999] text-center py-4 italic">No identities selected yet. Add some below!</p>
                  )}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <label className="text-black dark:text-white font-semibold">Browse & Add Identities</label>
                </div>
                <div className="space-y-5">
                  {Object.keys(identityData).sort((a, b) => a.localeCompare(b)).map((category) => {
                    const categoryIdentities = identityData[category] || [];
                    let userSelected = (profileData.identities || []).filter((id: string) => {
                      if (categoryIdentities.includes(id)) return true;
                      return categoryIdentities.some(cid => 
                        cid.toLowerCase().split('/').sort().join('/') === id.toLowerCase().split('/').sort().join('/') ||
                        cid.toLowerCase().split('/').includes(id.toLowerCase())
                      );
                    }).map(id => {
                      const exactMatch = categoryIdentities.find(cid => 
                        cid.toLowerCase() === id.toLowerCase() ||
                        cid.toLowerCase().split('/').sort().join('/') === id.toLowerCase().split('/').sort().join('/') ||
                        cid.toLowerCase().split('/').includes(id.toLowerCase())
                      );
                      return exactMatch || id;
                    });
                    
                    // Support legacy ethnicGroup if present and not in identities
                    if (category === 'Ethnic Groups' && userProfile.ethnicGroup) {
                      const match = categoryIdentities.find(id => {
                        const idLower = id.toLowerCase();
                        const userLower = userProfile.ethnicGroup?.toLowerCase() || '';
                        if (idLower === userLower) return true;
                        if (idLower.split('/').sort().join('/') === userLower.split('/').sort().join('/')) return true;
                        if (idLower.split('/').includes(userLower) || userLower.split('/').includes(idLower)) return true;
                        return false;
                      });
                      if (match && !userSelected.includes(match)) {
                        userSelected = [match, ...userSelected];
                      }
                    }
                    
                    const samples = categoryIdentities.filter((id: string) => !userSelected.includes(id)).sort((a, b) => a.localeCompare(b)).slice(0, 5);
                    return (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-bold text-black dark:text-white">{category}</h3>
                          <button
                            onClick={() => { setSelectedIdentityCategory(category); setShowIdentityModal(true); }}
                            className="px-3 py-1 rounded-full text-white text-xs font-bold shadow-md border-none"
                            style={{ backgroundImage: identityGradient }}
                          >
                            Show More →
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {userSelected.map((item: string) => (
                            <IdentityTag key={item} tag={item} variant="profile" />
                          ))}
                          {samples.map((item: string) => (
                            <IdentityTag key={item} tag={item} variant="standard" />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Profile Background Theme (Elite Only) */}
              <div className="space-y-4 p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 relative overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg text-black dark:text-white flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-linear-to-br from-amber-400 to-orange-500" />
                    </div>
                    Profile Background
                  </h3>
                  <div className="text-xs px-2 py-0.5 rounded-full border border-amber-500/30 text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/20 font-medium">
                    Elite Feature
                  </div>
                </div>
                <p className="text-sm text-[#666666] dark:text-[#a0a0a0] mb-4">
                  Choose a custom color theme for your profile.
                </p>

                <div className="grid grid-cols-4 gap-3">
                  {[
                    { id: 'default', color: 'bg-white', name: 'Default' },
                    { id: 'gold', color: 'bg-amber-400', name: 'Gold' },
                    { id: 'royal_blue', color: 'bg-blue-600', name: 'Royal' },
                    { id: 'burgundy', color: 'bg-rose-900', name: 'Burgundy' },
                    { id: 'forest_green', color: 'bg-green-700', name: 'Forest' },
                    { id: 'sand', color: 'bg-orange-100', name: 'Sand' },
                    { id: 'midnight', color: 'bg-slate-900', name: 'Midnight' },
                  ].map((theme) => (
                    <button
                      key={theme.id}
                      disabled={userProfile.subscriptionTier !== 'elite'}
                      onClick={() => {
                        // Direct update profile call or local state?
                        // User expects "Save" at top to persist, but let's persist theme immediately or handle in profile Data?
                        // Let's use updateProfile directly for immediate gratification or add to profileData?
                        // EditProfileScreen uses `profileData` local state but doesn't have `profileBackgroundTheme` in it yet.
                        // I'll add `profileBackgroundTheme` to profileData state or just call updateProfile directly for this setting (easiest for now as it's a specific toggle).
                        // better to add to local state for consistency with "Save" button.
                        // Wait, `profileData` refactoring is risky with lines.
                        // I will just call updateProfile directly for now or better, I will assume we want to save with the big Save button.
                        // But `handleSave` uses `profileData`. I need to add it to `profileData` to be safe.
                        // Let's use a separate local state for theme if I can't easily edit the big state object def.
                        // I'll use a direct update for this feature as it's "premium" and often instant.
                        updateProfile({ profileBackgroundTheme: theme.id as any });
                      }}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${userProfile.profileBackgroundTheme === theme.id
                        ? 'border-amber-500 scale-105 shadow-md'
                        : 'border-transparent hover:border-black/10'
                        }`}
                    >
                      <div className={`w-full h-full ${theme.color} opacity-80`} />
                      {userProfile.profileBackgroundTheme === theme.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Check className="w-6 h-6 text-white drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Lock Overlay for Non-Elite */}
                {userProfile.subscriptionTier !== 'elite' && (
                  <div className="absolute inset-0 bg-white/60 dark:bg-black/80 backdrop-blur-[2px] flex flex-col items-center justify-center text-center p-4 z-10">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-2">
                      <div className="w-6 h-6 text-amber-600 dark:text-amber-500" >🔒</div>
                    </div>
                    <h4 className="font-bold text-black dark:text-white mb-1">Unlock Themes</h4>
                    <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mb-3 max-w-[200px]">
                      Upgrade to Elite Yapper to customize your profile background.
                    </p>
                    <Button
                      className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs h-8"
                      onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'store' }))}
                    >
                      Upgrade to Elite
                    </Button>
                  </div>
                )}
              </div>

              {/* Friend List Visibility Settings */}
              <div className="space-y-4 p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10">
                <h3 className="text-lg text-black dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  Friend List Settings
                </h3>

                {/* Hide/Show Friend List Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-black dark:text-white mb-0.5">Hide Friend List</h4>
                    <p className="text-[11px] text-[#666666] dark:text-[#a0a0a0] leading-tight">
                      When enabled, your friends list will be hidden from everyone.
                    </p>
                  </div>
                  <Switch
                    checked={!showFriendList}
                    onCheckedChange={(checked) => setShowFriendList(!checked)}
                  />
                </div>

                {/* Friend List Visibility Dropdown */}
                {showFriendList && (
                  <div>
                    <label className="block text-black dark:text-white mb-2">Who Can See Your Friend List</label>
                    <Select
                      value={friendListVisibility}
                      onValueChange={(value: string) => setFriendListVisibility(value as any)}
                    >
                      <SelectTrigger className="w-full bg-[#e7d299] dark:bg-black/30 border-black/20 dark:border-white/20 text-black dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#e7d299] dark:bg-[#1a1a1a] border-black/20 dark:border-white/20">
                        <SelectItem value="everyone" className="text-black dark:text-white">
                          Everyone
                        </SelectItem>
                        <SelectItem value="friends" className="text-black dark:text-white">
                          Friends Only
                        </SelectItem>
                        <SelectItem value="private" className="text-black dark:text-white">
                          Only Me
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#999999] dark:text-[#808080] mt-2">
                      This controls who can see your complete friends list on your profile
                    </p>
                  </div>
                )}
              </div>

              {/* Tribe List Visibility Settings */}
              <div className="space-y-4 p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10">
                <h3 className="text-lg text-black dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  Tribe List Settings
                </h3>

                {/* Hide/Show Tribe List Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-black dark:text-white mb-0.5">Hide Tribe List</h4>
                    <p className="text-[11px] text-[#666666] dark:text-[#a0a0a0] leading-tight">
                      When enabled, your joined tribes will be hidden from everyone.
                    </p>
                  </div>
                  <Switch
                    checked={!showTribesList}
                    onCheckedChange={(checked) => setShowTribesList(!checked)}
                  />
                </div>

                {/* Tribe List Visibility Dropdown */}
                {showTribesList && (
                  <div>
                    <label className="block text-black dark:text-white mb-2">Who Can See Your Tribe List</label>
                    <Select
                      value={tribeListVisibility}
                      onValueChange={(value: string) => setTribeListVisibility(value as any)}
                    >
                      <SelectTrigger className="w-full bg-[#e7d299] dark:bg-black/30 border-black/20 dark:border-white/20 text-black dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#e7d299] dark:bg-[#1a1a1a] border-black/20 dark:border-white/20">
                        <SelectItem value="everyone" className="text-black dark:text-white">
                          Everyone
                        </SelectItem>
                        <SelectItem value="friends" className="text-black dark:text-white">
                          Friends Only
                        </SelectItem>
                        <SelectItem value="private" className="text-black dark:text-white">
                          Only Me
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#999999] dark:text-[#808080] mt-2">
                      This controls who can see your tribes on your profile
                    </p>
                  </div>
                )}
              </div>

              {/* Yap Feed Visibility Settings */}
              <div className="space-y-4 p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10">
                <h3 className="text-lg text-black dark:text-white flex items-center gap-2">
                  <Library className="w-5 h-5 text-[#4478ea]" />
                  Yap Feed Settings
                </h3>

                {/* Hide/Show Yaps Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-black dark:text-white mb-0.5">Hide Yaps</h4>
                    <p className="text-[11px] text-[#666666] dark:text-[#a0a0a0] leading-tight">
                      When enabled, your public yap feed will be hidden from everyone.
                    </p>
                  </div>
                  <Switch
                    checked={!showYaps}
                    onCheckedChange={(checked) => setShowYaps(!checked)}
                  />
                </div>
              </div>


              {/* Show in Discover Settings */}
              <div className="space-y-4 p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10">
                <h3 className="text-lg text-black dark:text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#4478ea]" />
                  Discover Visibility
                </h3>

                {/* Show/Hide in Discover Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-black dark:text-white mb-1">Show in Discover</p>
                    <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                      Control whether others can see your profile in the discover section
                    </p>
                  </div>
                  <Switch
                    checked={showInDiscover}
                    onCheckedChange={(checked) => setShowInDiscover(checked)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      <IdentityExpansionModal
        isOpen={showIdentityModal}
        onClose={() => setShowIdentityModal(false)}
        category={selectedIdentityCategory}
        onSave={handleIdentitiesSave}
        initialSelected={profileData.identities || []}
      />


      <ImageCropperModal
        isOpen={cropModalOpen || false}
        onClose={() => setCropModalOpen(false)}
        imageUrl={cropImage || ''}
        onSave={handleCropSave}
        aspectRatio={cropType === 'banner' ? 1200 / 300 : 1}
        title="Adjust Banner"
        description="Fit the image into the 1200x300 frame"
        circular={false}
      />

      <MediaTrimmerModal
        isOpen={mediaModalOpen}
        onClose={() => {
          setMediaModalOpen(false);
          setMediaUrl(null);
          setOriginalMediaBlob(null);
          setTempMatchPhoto(null);
          setTempVideoBlob(null);
          setTempTrimStart(undefined);
          setTempTrimEnd(undefined);
          setMediaStep('match');
        }}
        mediaUrl={mediaUrl || ''}
        mediaBlob={originalMediaBlob || undefined}
        mediaType={mediaType}
        onSave={handleMediaSave}
        aspectRatio={mediaStep === 'match' ? 3 / 4 : 1}
        title={mediaStep === 'match' ? "Full Card Preview" : "Avatar Display"}
        maxDurationSec={300}
        circular={false}
      />
    </div>
  );
}
